import { Component } from '@theme/component';
import { isRTL, prefersReducedMotion } from '@theme/utilities';

const LARGE_SCREEN = '(min-width: 750px)';
const DRAG_THRESHOLD = 4;
const INERTIA_FRICTION = 0.95;
const INERTIA_MIN_VELOCITY = 0.02;
const SNAP_THRESHOLD = 8;

/**
 * Mixed-width image slider — same drag / inertia feel as sliding-grid.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} scroller - The scrollable track.
 * @property {HTMLButtonElement} [previous] - Previous control.
 * @property {HTMLButtonElement} [next] - Next control.
 *
 * @extends Component<Refs>
 */
class ImageSlider extends Component {
  requiredRefs = ['scroller'];

  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {MediaQueryList} */
  #largeMediaQuery = window.matchMedia(LARGE_SCREEN);

  /** @type {boolean} */
  #dragging = false;

  /** @type {number} */
  #inertiaFrame = 0;

  /** @type {number|undefined} */
  #interval = undefined;

  /** @type {IntersectionObserver | null} */
  #imageObserver = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  connectedCallback() {
    super.connectedCallback();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    const { scroller } = this.refs;
    scroller.addEventListener('scroll', this.#onScroll, { passive: true, signal });
    scroller.addEventListener('pointerdown', this.#onPointerDown, { signal });
    window.addEventListener('resize', this.#onWindowResize, { passive: true, signal });
    this.#largeMediaQuery.addEventListener('change', this.#onBreakpointChange, { signal });
    this.addEventListener('mouseenter', this.suspend, { signal });
    this.addEventListener('mouseleave', this.resume, { signal });
    document.addEventListener('visibilitychange', this.#handleVisibilityChange, { signal });

    this.#observeImages();
    this.#observeResize();
    requestAnimationFrame(() => {
      if (!this.isConnected) return;
      this.#syncImageSizes();
      this.#updateControls();
    });
    this.play();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.suspend();
    this.#stopInertia();
    this.#imageObserver?.disconnect();
    this.#imageObserver = null;
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    this.#abortController?.abort();
  }

  previous() {
    if (!this.#isSliderEnabled()) return;
    this.#scrollBy(-1);
    this.#restartAutoplay();
  }

  next() {
    if (!this.#isSliderEnabled()) return;
    this.#scrollBy(1);
    this.#restartAutoplay();
  }

  /**
   * @param {number} [interval]
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay || this.#interval || prefersReducedMotion()) return;
    if (!this.#isSliderEnabled() || !interval) return;

    this.paused = false;
    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden || this.#dragging) return;
      this.#autoplayNext();
    }, interval);
  }

  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(value) {
    this.toggleAttribute('paused', Boolean(value));
  }

  suspend = () => {
    clearInterval(this.#interval);
    this.#interval = undefined;
  };

  resume = () => {
    if (!this.autoplay || this.paused) return;
    this.suspend();
    this.play();
  };

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = Number.parseInt(`${interval}`, 10);
    if (Number.isNaN(value)) return undefined;
    return value * 1000;
  }

  #restartAutoplay() {
    if (!this.autoplay || this.paused) return;
    this.suspend();
    this.play();
  }

  #autoplayNext() {
    const { scroller } = this.refs;
    if (!scroller || !this.#isSliderEnabled()) return;

    const maxScroll = scroller.scrollWidth - scroller.clientWidth;
    if (maxScroll <= 2) return;

    const atEnd = Math.abs(scroller.scrollLeft) >= maxScroll - 2;
    if (atEnd) {
      this.#scrollToStart();
      return;
    }

    this.#scrollBy(1);
  }

  #scrollToStart() {
    const { scroller } = this.refs;
    if (!scroller) return;

    this.#stopInertia();
    scroller.scrollTo({
      left: 0,
      behavior: prefersReducedMotion() ? 'instant' : 'smooth',
    });
  }

  #onBreakpointChange = () => {
    this.#syncImageSizes();
    this.#updateControls();
    if (this.#isSliderEnabled()) {
      this.resume();
    } else {
      this.suspend();
    }
  };

  #onWindowResize = () => {
    this.#syncImageSizes();
    this.#updateControls();
  };

  /**
   * Point srcset at the rendered slide width so the browser fetches one matching size.
   */
  #syncImageSizes() {
    this.querySelectorAll('.image-slider__item').forEach((item) => {
      if (!(item instanceof HTMLElement)) return;
      const image = item.querySelector('img.image-slider__image');
      if (!(image instanceof HTMLImageElement)) return;
      const width = Math.round(item.getBoundingClientRect().width);
      if (width < 2) return;
      const sizes = `${width}px`;
      if (image.sizes !== sizes) image.sizes = sizes;
    });
  }

  #observeResize() {
    this.#resizeObserver?.disconnect();
    if (!('ResizeObserver' in window)) return;

    this.#resizeObserver = new ResizeObserver(() => {
      this.#syncImageSizes();
    });
    this.#resizeObserver.observe(this);
  }

  #handleVisibilityChange = () => (document.hidden ? this.suspend() : this.resume());

  /**
   * Load lazy images once they are near the visible area (page or slider scroll).
   */
  #observeImages() {
    if (!('IntersectionObserver' in window)) return;

    const images = [...this.querySelectorAll('img.image-slider__image[loading="lazy"]')];
    if (!images.length) return;

    this.#imageObserver?.disconnect();
    this.#imageObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const image = entry.target;
          if (image instanceof HTMLImageElement) {
            image.loading = 'eager';
          }
          this.#imageObserver?.unobserve(image);
        }
      },
      {
        root: null,
        rootMargin: '200px 400px',
        threshold: 0.01,
      }
    );

    for (const image of images) {
      this.#imageObserver.observe(image);
    }
  }

  #isSliderEnabled() {
    const isDesktop = this.#largeMediaQuery.matches;
    const attr = isDesktop ? 'data-slider-desktop' : 'data-slider-mobile';
    return this.getAttribute(attr) === 'true';
  }

  /**
   * @returns {HTMLElement[]}
   */
  #getItems() {
    const { scroller } = this.refs;
    const track = scroller?.querySelector('.image-slider__track');
    return /** @type {HTMLElement[]} */ ([...(track?.querySelectorAll('.image-slider__item') ?? [])]);
  }

  /**
   * @returns {number}
   */
  #getStartPadding() {
    const { scroller } = this.refs;
    const styles = getComputedStyle(scroller);
    return parseFloat(styles.paddingInlineStart) || parseFloat(styles.paddingLeft) || 0;
  }

  /**
   * Scroll position that aligns an item to the padded start edge.
   * @param {HTMLElement} item
   * @returns {number}
   */
  #getItemScrollLeft(item) {
    const { scroller } = this.refs;
    const padding = this.#getStartPadding();
    const rtl = isRTL(scroller);
    const scrollerRect = scroller.getBoundingClientRect();
    const itemRect = item.getBoundingClientRect();
    const delta = rtl
      ? scrollerRect.right - padding - itemRect.right
      : itemRect.left - scrollerRect.left - padding;

    return scroller.scrollLeft + delta;
  }

  /**
   * @returns {number}
   */
  #getCurrentIndex() {
    const { scroller } = this.refs;
    const items = this.#getItems();
    if (!items.length) return 0;

    const padding = this.#getStartPadding();
    const rtl = isRTL(scroller);
    const scrollerRect = scroller.getBoundingClientRect();
    let currentIndex = 0;

    items.forEach((item, index) => {
      const itemRect = item.getBoundingClientRect();
      const offset = rtl
        ? scrollerRect.right - padding - itemRect.right
        : itemRect.left - scrollerRect.left - padding;

      if (offset <= SNAP_THRESHOLD) currentIndex = index;
    });

    return currentIndex;
  }

  /**
   * @param {number} direction - -1 for previous, 1 for next
   */
  #scrollBy(direction) {
    const { scroller } = this.refs;
    if (!scroller) return;

    const items = this.#getItems();
    if (!items.length) return;

    this.#stopInertia();

    const rtl = isRTL(scroller);
    const maxScroll = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
    const currentIndex = this.#getCurrentIndex();
    const targetIndex = Math.max(0, Math.min(items.length - 1, currentIndex + direction));
    let target = this.#getItemScrollLeft(items[targetIndex]);

    if (direction > 0 && targetIndex === items.length - 1) {
      target = rtl ? -maxScroll : maxScroll;
    } else if (direction < 0 && targetIndex === 0) {
      target = 0;
    }

    const min = rtl ? -maxScroll : 0;
    const max = rtl ? 0 : maxScroll;
    target = Math.max(min, Math.min(max, target));

    scroller.scrollTo({
      left: target,
      behavior: prefersReducedMotion() ? 'instant' : 'smooth',
    });
  }

  #onScroll = () => {
    this.#updateControls();
  };

  #stopInertia() {
    if (this.#inertiaFrame) {
      cancelAnimationFrame(this.#inertiaFrame);
      this.#inertiaFrame = 0;
    }
  }

  /**
   * @param {number} velocity - px per ms
   */
  #startInertia(velocity) {
    const { scroller } = this.refs;
    if (!scroller || prefersReducedMotion()) {
      this.#updateControls();
      return;
    }

    let v = velocity;
    const maxScroll = scroller.scrollWidth - scroller.clientWidth;
    let last = performance.now();

    const step = (now) => {
      const dt = Math.min(now - last, 32);
      last = now;

      v *= Math.pow(INERTIA_FRICTION, dt / 16);
      if (Math.abs(v) < INERTIA_MIN_VELOCITY) {
        this.#inertiaFrame = 0;
        this.#updateControls();
        return;
      }

      const rtl = isRTL(scroller);
      const min = rtl ? -maxScroll : 0;
      const max = rtl ? 0 : maxScroll;
      const next = Math.max(min, Math.min(max, scroller.scrollLeft + v * dt));
      scroller.scrollLeft = next;

      if (next <= min || next >= max) {
        this.#inertiaFrame = 0;
        this.#updateControls();
        return;
      }

      this.#inertiaFrame = requestAnimationFrame(step);
    };

    this.#inertiaFrame = requestAnimationFrame(step);
  }

  /**
   * Pointer drag for mouse / pen. Touch uses native overflow momentum.
   * @param {PointerEvent} event
   */
  #onPointerDown = (event) => {
    if (!this.#isSliderEnabled() || this.#dragging) return;
    if (event.button !== 0) return;
    if (!(event.target instanceof Element)) return;

    if (
      event.target.closest(
        'button, input, select, textarea, label, summary, [contenteditable="true"]'
      )
    ) {
      return;
    }

    this.suspend();

    if (event.pointerType === 'touch') {
      const resumeOnUp = () => {
        document.removeEventListener('pointerup', resumeOnUp);
        document.removeEventListener('pointercancel', resumeOnUp);
        this.resume();
      };
      document.addEventListener('pointerup', resumeOnUp);
      document.addEventListener('pointercancel', resumeOnUp);
      return;
    }

    const { scroller } = this.refs;
    if (!scroller) return;

    this.#stopInertia();

    const startX = event.clientX;
    const startScrollLeft = scroller.scrollLeft;
    let previousX = startX;
    let previousTime = performance.now();
    let velocity = 0;
    let moved = false;

    const controller = new AbortController();
    const { signal } = controller;

    /**
     * @param {PointerEvent} moveEvent
     */
    const onPointerMove = (moveEvent) => {
      const deltaX = moveEvent.clientX - startX;

      if (!moved) {
        if (Math.abs(deltaX) < DRAG_THRESHOLD) return;
        moved = true;
        this.#dragging = true;
        this.setAttribute('dragging', '');
        scroller.setPointerCapture(moveEvent.pointerId);
        document.addEventListener('click', preventClick, { capture: true, once: true, signal });
      }

      const now = performance.now();
      const frameDelta = moveEvent.clientX - previousX;
      const dt = Math.max(now - previousTime, 1);
      velocity = velocity * 0.6 + (-frameDelta / dt) * 0.4;
      previousX = moveEvent.clientX;
      previousTime = now;

      moveEvent.preventDefault();
      scroller.scrollLeft = startScrollLeft - deltaX;
    };

    /**
     * @param {PointerEvent} upEvent
     */
    const onPointerUp = (upEvent) => {
      controller.abort();
      this.#dragging = false;
      this.removeAttribute('dragging');

      if (moved && scroller.hasPointerCapture(upEvent.pointerId)) {
        scroller.releasePointerCapture(upEvent.pointerId);
      }

      if (moved && Math.abs(velocity) > INERTIA_MIN_VELOCITY) {
        this.#startInertia(velocity);
      } else {
        this.#updateControls();
      }

      if (!this.matches(':hover')) this.resume();
    };

    /**
     * @param {Event} clickEvent
     */
    const preventClick = (clickEvent) => {
      clickEvent.preventDefault();
      clickEvent.stopPropagation();
    };

    document.addEventListener('pointermove', onPointerMove, { signal });
    document.addEventListener('pointerup', onPointerUp, { signal });
    document.addEventListener('pointercancel', onPointerUp, { signal });
  };

  #updateControls() {
    const { scroller, previous, next } = this.refs;
    if (!scroller) return;

    const sliderEnabled = this.#isSliderEnabled();
    const maxScroll = scroller.scrollWidth - scroller.clientWidth;
    const offset = Math.abs(scroller.scrollLeft);
    const atStart = offset <= 2;
    const atEnd = offset >= maxScroll - 2;
    const canScroll = sliderEnabled && maxScroll > 2;

    if (!sliderEnabled && scroller.scrollLeft !== 0) {
      scroller.scrollLeft = 0;
    }

    if (previous instanceof HTMLButtonElement) {
      previous.disabled = !canScroll || atStart;
      previous.setAttribute('aria-disabled', String(!canScroll || atStart));
    }

    if (next instanceof HTMLButtonElement) {
      next.disabled = !canScroll || atEnd;
      next.setAttribute('aria-disabled', String(!canScroll || atEnd));
    }

    this.toggleAttribute('data-can-scroll', canScroll);
  }
}

if (!customElements.get('image-slider')) {
  customElements.define('image-slider', ImageSlider);
}
